import type { ComponentProps, ReactNode } from "react";
import { Adapt, Sheet, SizeTokens, withStaticProperties } from "tamagui";

import { Dialog } from "@/components/Dialog";
import TemplatedDialogContent from "./TemplatedDialogContent";
import TemplatedDialogHeader, {
  TemplatedDialogControls,
} from "./TemplatedDialogHeader";

type DialogContentProps = ComponentProps<typeof Dialog.Content>;

type TemplatedDialogProps = {
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: SizeTokens;
  height?: SizeTokens;
} & Omit<DialogContentProps, "children">;

const TemplatedDialogBase = ({
  children,
  open,
  onOpenChange,
  width = "$constraint",
  height = "$modalHeight",
  ...contentProps
}: TemplatedDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange} modal disableRemoveScroll>
    <Dialog.Adapt when="maxLg">
      <Sheet modal snapPoints={[98]} disableDrag dismissOnSnapToBottom={false}>
        <Sheet.Frame flex={1}>
          <Adapt.Contents />
        </Sheet.Frame>
        <Sheet.Overlay
          transition="standard"
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
      </Sheet>
    </Dialog.Adapt>

    <Dialog.Portal>
      <Dialog.Overlay
        key="overlay"
        backgroundColor="$fillOverlay"
        backdropFilter="blur(8px)"
      />
      <Dialog.FocusScope key="content" trapped loop>
        <Dialog.Content
          maxWidth="$constraint"
          height="$modalHeight"
          width="100%"
          padding={0}
          overflow="hidden"
          {...contentProps}
        >
          {children}
        </Dialog.Content>
      </Dialog.FocusScope>
    </Dialog.Portal>
  </Dialog>
);

const TemplatedDialog = withStaticProperties(TemplatedDialogBase, {
  Header: TemplatedDialogHeader,
  Controls: TemplatedDialogControls,
  Content: TemplatedDialogContent,
});

export default TemplatedDialog;
